import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/Button/Button'
import { Checkbox } from '@/components/Checkbox'
import { useCorpStore } from '@/reducers/corporateReducer'
import { useCallback, useState } from 'react'
import axiosInstance from '@/requests/axios'
import { updateCorporate } from '@/requests/corporate'
import { useEnrollmentHydration } from '@/hooks/useEnrollmentHydration'
import { ROUTES } from '../CorporateSignupPanel'

interface ValidationError {
  isPep?: string
}

export default function DirectorPepInfo() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const nextState = useCorpStore((state) => state.nextState)
  const isPep = useCorpStore((state) => state.isPep)
  const setIsPep = useCorpStore((state) => state.setIsPep)

  const [errors, setErrors] = useState<ValidationError>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEnrollmentHydration(
    useCallback(
      (enrollment) => {
        if (enrollment.isPep !== null) setIsPep(enrollment.isPep)
      },
      [setIsPep]
    )
  )

  // Determine if form is valid (an option is selected)
  const isFormValid = isPep !== null && isPep !== undefined

  const validateForm = (): ValidationError => {
    const newErrors: ValidationError = {}

    if (!isFormValid) {
      newErrors.isPep = t('directorPepInfo.errors.selectionRequired')
    }

    return newErrors
  }

  const handlePepChange = (value: boolean) => {
    setIsPep(value)
    setTouched((prev) => ({ ...prev, isPep: true }))
    setErrors({}) // Clear errors when selection is made
  }

  const handleSubmit = async () => {
    if (isPep !== false || isSubmitting) return

    setIsSubmitting(true)
    const formErrors = validateForm()
    setErrors(formErrors)
    setTouched({ isPep: true })

    if (Object.keys(formErrors).length === 0) {
      try {
        await updateCorporate({ isPep: false })

        nextState()
        navigate(ROUTES.DIRECTOR_AUTH_INFO)
      } catch (error) {
        console.error('Error updating PEP status:', error)
        setErrors({
          isPep: t('directorPepInfo.errors.submitError'),
        })
      } finally {
        setIsSubmitting(false)
      }
    } else {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="text-pep-container">
      <div
        className="text-lg font-normal uppercase"
        data-testid="text-pep-title"
      >
        {t('directorPepInfo.title')}
      </div>

      <div
        className="flex flex-col gap-4"
        data-testid="text-pep-question-container"
      >
        <p className="text-base" data-testid="text-pep-question-text">
          {t('directorPepInfo.question')}
          <a
            href="https://faq.paytrie.com/help/what-is-a-politically-exposed-person-pep"
            className="text-blue-600"
            data-testid="text-pep-pep-link"
          >
            {t('directorPepInfo.pepLink')}
          </a>
          {t('directorPepInfo.questionMiddle')}
          <a
            href="https://faq.paytrie.com/help/what-is-a-politically-exposed-person-pep"
            className="text-blue-600"
            data-testid="text-pep-hio-link"
          >
            {t('directorPepInfo.hioLink')}
          </a>
          {t('directorPepInfo.questionEnd')}
        </p>

        <div
          className="flex items-center gap-8"
          data-testid="text-pep-options-container"
        >
          <Checkbox
            label={t('directorPepInfo.yes')}
            checked={isPep === true}
            onCheckedChange={() => handlePepChange(true)}
            data-testid="text-pep-yes-checkbox"
          />
          <Checkbox
            label={t('directorPepInfo.no')}
            checked={isPep === false}
            onCheckedChange={() => handlePepChange(false)}
            data-testid="text-pep-no-checkbox"
          />
        </div>
        {isPep === true && (
          <span
            className="text-sm text-red-500 mt-1"
            data-testid="text-pep-blocked"
          >
            {t('directorPepInfo.pepBlocked')}
          </span>
        )}
        {touched.isPep && errors.isPep && (
          <span
            className="text-sm text-red-500 mt-1"
            data-testid="text-pep-error"
          >
            {errors.isPep}
          </span>
        )}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={isPep !== false || isSubmitting}
        className="mt-4 inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
        data-testid="text-pep-submit-button"
      >
        {isSubmitting
          ? t('directorPepInfo.submitButton.processing')
          : t('directorPepInfo.submitButton.continue')}
      </Button>
    </div>
  )
}

/*
Component Improvement Suggestions:
- Add proper heading levels for better document outline
- Consider using radio buttons for Yes/No selection to provide better accessibility
- Include more detailed explanation about what PEP/HIO means for better user understanding
- Add ARIA attributes to improve screen reader experience
- Add keyboard focus management for better navigation
- Consider adding examples of what constitutes a PEP/HIO for clarity
- Improve error message display with aria-live regions
- Use fieldset and legend elements for form grouping
- Add visible focus states for interactive elements
- Provide better form validation feedback
*/
